import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { emailRegex, ROUTES } from '../../../assets/constants/strings';
import {
  provisionApiTrial,
  provisionApiProduction,
  provisionReaderTrial,
  provisionReaderEnterprise,
  setApiQuota,
  updateEnquiryStatus,
} from '../../../services/axios';

const mapStateToProps = (state) => ({
  token: state.user.user ? state.user.user.token : null,
});

const STEPS = ['Organisation', 'Products', 'Access', 'Commercial', 'Review'];

const PRODUCTS = [
  {
    key: 'app',
    name: 'RegulationCity App',
    desc: 'Regulatory intelligence platform',
    trialMeta: 'free · capped',
    entMeta: 'seats · we set price',
    dot: 'app',
  },
  {
    key: 'api',
    name: 'Developer API',
    desc: 'Programmatic access to the data',
    trialMeta: '100 lifetime calls',
    entMeta: 'uncapped / negotiated',
    dot: 'api',
  },
];

// A blank wizard model. `prefill` (from an enquiry) seeds the organisation + a sensible product.
const blankModel = (prefill = {}) => ({
  org: {
    name: prefill.company || '',
    first: prefill.firstName || '',
    last: prefill.lastName || '',
    email: prefill.email || '',
    domains: '',
  },
  products: {
    app: { on: prefill.product === 'ReaderApp', tier: 'enterprise' },
    api: { on: prefill.product === 'DeveloperApi', tier: 'trial' },
  },
  access: {
    appSeats: '',
    apiSeats: '',
    apiLimit: '',
    apiInterval: 'Monthly',
    adminSame: true,
    adminEmail: '',
    adminFirst: '',
    adminLast: '',
  },
  comm: {
    app: { price: '', interval: 'Annual', start: '', end: '', po: '', notes: '' },
    api: { price: '', interval: 'Annual', start: '', end: '', po: '', notes: '' },
  },
});

// Admin: provision a customer in one guided pass. Unifies the old API-only ProvisionTrial form and
// the enterprise terms that used to live only in the customer drawer, and extends provisioning to
// the reader App. Orchestrates the existing provision-* endpoints per selected product/tier:
// create-by-email (trial) → capture clientId → promote to enterprise with the full terms. Optionally
// launched pre-filled from an enquiry, which it marks Handled on success.
const ProvisionWizard = ({ token, prefill }) => {
  const [step, setStep] = useState(0);
  const [model, setModel] = useState(() => blankModel(prefill));
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null); // per-product [{product,tier,ok,reason}]
  const [error, setError] = useState(null);

  const selected = PRODUCTS.filter((p) => model.products[p.key].on);
  const isEnt = (k) => model.products[k].on && model.products[k].tier === 'enterprise';
  const anyEnterprise = () => isEnt('app') || isEnt('api');
  // Seats map to the org-level user-login cap (Client.ActiveUserTier), which is a single number
  // for the whole organisation — NOT per product. So when both products are Enterprise we collect
  // ONE org-wide seat count (held in appSeats) and apply it to both, rather than two figures that
  // would fight over the same cap.
  const bothEnt = isEnt('app') && isEnt('api');
  const seatsFor = (k) => (bothEnt || k === 'app' ? model.access.appSeats : model.access.apiSeats);

  // Shallow-merge helpers keep the JSX terse.
  const setOrg = (patch) => setModel((m) => ({ ...m, org: { ...m.org, ...patch } }));
  const setAccess = (patch) => setModel((m) => ({ ...m, access: { ...m.access, ...patch } }));
  const setComm = (k, patch) =>
    setModel((m) => ({ ...m, comm: { ...m.comm, [k]: { ...m.comm[k], ...patch } } }));
  const toggleProduct = (k) =>
    setModel((m) => ({
      ...m,
      products: { ...m.products, [k]: { ...m.products[k], on: !m.products[k].on } },
    }));
  const setTier = (k, tier) =>
    setModel((m) => ({ ...m, products: { ...m.products, [k]: { ...m.products[k], tier } } }));

  const orgValid = model.org.name.trim() && emailRegex(model.org.email);

  const goNext = () => {
    if (step === 0 && !orgValid) return;
    if (step === 1 && selected.length === 0) return;
    // Commercial (step 3) only applies to Enterprise products — skip it for trial-only deals.
    if (step === 2 && !anyEnterprise()) {
      setStep(4);
      return;
    }
    setStep((s) => Math.min(4, s + 1));
  };
  const goBack = () => {
    if (step === 4 && !anyEnterprise()) {
      setStep(2);
      return;
    }
    setStep((s) => Math.max(0, s - 1));
  };

  // The enterprise "extra" payload shared by both product promotions.
  const enterpriseExtra = (k) => {
    const c = model.comm[k];
    const adminSame = model.access.adminSame;
    const seats = seatsFor(k);
    return {
      seats: seats === '' ? null : parseInt(seats, 10),
      adminEmail: adminSame ? null : model.access.adminEmail.trim() || null,
      adminFirstName: adminSame ? null : model.access.adminFirst.trim() || null,
      adminLastName: adminSame ? null : model.access.adminLast.trim() || null,
      startDate: c.start || null,
      endDate: c.end || null,
      purchaseOrder: c.po.trim() || null,
      notes: c.notes.trim() || null,
      allowedDomains: model.org.domains.trim() || null,
    };
  };

  const basePayload = () => ({
    email: model.org.email.trim(),
    firstName: model.org.first.trim(),
    lastName: model.org.last.trim(),
    company: model.org.name.trim(),
  });

  // Provision one product. Trial = a single create/invite call. Enterprise = create (to find/create
  // the client + issue the invite) then promote with the negotiated terms; the client is shared
  // across products because the create endpoints find-or-create by email.
  const provisionProduct = async (k) => {
    const label = k === 'app' ? 'RegulationCity App' : 'Developer API';
    const ent = isEnt(k);
    try {
      if (k === 'app') {
        const created = await provisionReaderTrial(token, basePayload());
        if (!created.success || !created.data) throw new Error(created.reason || 'Could not create the account.');
        if (!ent) return { product: label, tier: 'Trial', ok: true, reason: created.reason };
        const clientId = created.data.clientId;
        const c = model.comm.app;
        const promoted = await provisionReaderEnterprise(
          token,
          clientId,
          c.price === '' ? null : parseFloat(c.price),
          c.interval,
          enterpriseExtra('app')
        );
        if (!promoted.success) throw new Error(promoted.reason || 'Created the account but could not apply Enterprise terms.');
        return { product: label, tier: 'Enterprise', ok: true, reason: promoted.reason };
      }
      // Developer API
      const created = await provisionApiTrial(token, { ...basePayload(), production: ent });
      if (!created.success || !created.data) throw new Error(created.reason || 'Could not create the account.');
      if (!ent) return { product: label, tier: 'Trial', ok: true, reason: created.reason };
      const clientId = created.data.clientId;
      const c = model.comm.api;
      const promoted = await provisionApiProduction(
        token,
        clientId,
        c.price === '' ? null : parseFloat(c.price),
        c.interval,
        enterpriseExtra('api')
      );
      if (!promoted.success) throw new Error(promoted.reason || 'Created the account but could not apply Enterprise terms.');
      // Optional negotiated call limit (interval-aware) — cleared/uncapped when left blank.
      if (model.access.apiLimit !== '') {
        await setApiQuota(token, clientId, parseInt(model.access.apiLimit, 10), model.access.apiInterval);
      }
      return { product: label, tier: 'Enterprise', ok: true, reason: promoted.reason };
    } catch (e) {
      return { product: label, tier: ent ? 'Enterprise' : 'Trial', ok: false, reason: e.message };
    }
  };

  const submit = async () => {
    if (busy || !token || selected.length === 0) return;
    setBusy(true);
    setError(null);
    // Sequential so a shared client is created once and reused by the second product.
    const out = [];
    for (const p of selected) out.push(await provisionProduct(p.key));
    // Close the loop: a fully-successful provision from an enquiry marks that enquiry Handled.
    if (out.every((r) => r.ok) && prefill && prefill.enquiryId) {
      await updateEnquiryStatus(token, prefill.enquiryId, 'Handled');
    }
    setBusy(false);
    setResults(out);
  };

  const reset = () => {
    setModel(blankModel());
    setResults(null);
    setError(null);
    setStep(0);
  };

  // ---- result screen ---------------------------------------------------------
  if (results) {
    const allOk = results.every((r) => r.ok);
    return (
      <section className='adm-section'>
        <div className='adm-panel'>
          <div className={`adm-alert ${allOk ? 'adm-alert--ok' : 'adm-alert--warn'}`} style={{ marginBottom: '1rem' }}>
            {allOk ? '✓ Customer provisioned' : 'Provisioned with issues — review below'}
          </div>
          <div className='pw-result'>
            {results.map((r) => (
              <div key={r.product} className='pw-result__row'>
                <span className={`pw-result__ico ${r.ok ? 'is-ok' : 'is-bad'}`}>{r.ok ? '✓' : '!'}</span>
                <div>
                  <div className='pw-result__name'>
                    {r.product} <span className={`adm-badge ${r.tier === 'Enterprise' ? 'adm-badge--prod' : 'adm-badge--trial'}`}>{r.tier}</span>
                  </div>
                  <div className='pw-result__msg'>{r.reason}</div>
                </div>
              </div>
            ))}
          </div>
          <p className='adm-note' style={{ marginTop: '1rem' }}>
            A set-password invite has been emailed to {model.org.email}. Manage this customer any time from{' '}
            <Link to={`${ROUTES.APIAdmin}/customers`} className='adm-link'>Customers →</Link>
          </p>
          <div className='adm-actions'>
            <button className='adm-btn adm-btn--primary' type='button' onClick={reset}>Provision another</button>
          </div>
        </div>
      </section>
    );
  }

  // ---- step bodies -----------------------------------------------------------
  const stepper = (
    <div className='pw-stepper'>
      {STEPS.map((s, i) => {
        const cls = i === step ? 'is-active' : i < step ? 'is-done' : '';
        return (
          <div key={s} className={`pw-step ${cls}`}>
            <span className='pw-step__n'>{i < step ? '✓' : i + 1}</span>
            <span className='pw-step__l'>{s}</span>
          </div>
        );
      })}
    </div>
  );

  const orgStep = (
    <>
      <div className='pw-title'>Organisation</div>
      <p className='pw-hint'>Who we're setting up.{prefill ? ' Pre-filled from the enquiry — edit anything.' : ''}</p>
      <div className='adm-grid'>
        <div className='adm-field' style={{ gridColumn: '1 / -1' }}>
          <div className='adm-field__label'>Organisation name *</div>
          <input className='adm-input' value={model.org.name} onChange={(e) => setOrg({ name: e.target.value })} placeholder='Acme Financial Services' />
        </div>
        <div className='adm-field'>
          <div className='adm-field__label'>Primary contact — first name</div>
          <input className='adm-input' value={model.org.first} onChange={(e) => setOrg({ first: e.target.value })} />
        </div>
        <div className='adm-field'>
          <div className='adm-field__label'>Last name</div>
          <input className='adm-input' value={model.org.last} onChange={(e) => setOrg({ last: e.target.value })} />
        </div>
        <div className='adm-field' style={{ gridColumn: '1 / -1' }}>
          <div className='adm-field__label'>Work email *</div>
          <input className='adm-input' type='email' value={model.org.email} onChange={(e) => setOrg({ email: e.target.value })} placeholder='contact@company.com' />
          <span className='pw-fieldhint'>The set-password invite goes here. This person becomes the org admin unless you change it in Access.</span>
        </div>
        <div className='adm-field' style={{ gridColumn: '1 / -1' }}>
          <div className='adm-field__label'>Organisation email domains</div>
          <input className='adm-input' value={model.org.domains} onChange={(e) => setOrg({ domains: e.target.value })} placeholder='acme.com, acme.com.au' />
          <span className='pw-fieldhint'>Comma-separated. Recorded on the organisation (Enterprise).</span>
        </div>
      </div>
    </>
  );

  const productStep = (
    <>
      <div className='pw-title'>Products</div>
      <p className='pw-hint'>Pick one or both. Choose a tier per product — a dual-product deal is provisioned in one pass.</p>
      <div className='pw-prodgrid'>
        {PRODUCTS.map((p) => {
          const on = model.products[p.key].on;
          const tier = model.products[p.key].tier;
          return (
            <div key={p.key} className={`pw-prod ${on ? 'is-on' : ''}`}>
              <div className='pw-prod__top' onClick={() => toggleProduct(p.key)}>
                <span className={`pw-prod__ico pw-prod__ico--${p.dot}`} />
                <div>
                  <div className='pw-prod__name'>{p.name}</div>
                  <div className='pw-prod__desc'>{p.desc}</div>
                </div>
                <span className='pw-prod__chk'>{on ? '✓' : ''}</span>
              </div>
              <div className='pw-tiers'>
                {[
                  { k: 'trial', label: 'Trial', meta: p.trialMeta },
                  { k: 'enterprise', label: 'Enterprise', meta: p.entMeta },
                ].map((t) => (
                  <button
                    key={t.k}
                    type='button'
                    className={`pw-tier ${tier === t.k ? 'is-sel' : ''}`}
                    disabled={!on}
                    onClick={() => setTier(p.key, t.k)}
                  >
                    {t.label}
                    <small>{t.meta}</small>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {selected.length === 0 && <p className='pw-empty'>Select at least one product to continue.</p>}
    </>
  );

  const accessStep = (
    <>
      <div className='pw-title'>Access &amp; the org admin</div>
      <p className='pw-hint'>Seats, limits and who administers the organisation.</p>
      {model.products.app.on && (
        <div className='pw-block'>
          <div className='pw-block__h'>
            <span className='pw-dot pw-dot--app' /> RegulationCity App
            <span className={`adm-badge ${isEnt('app') ? 'adm-badge--prod' : 'adm-badge--trial'}`}>{isEnt('app') ? 'Enterprise' : 'Trial'}</span>
          </div>
          {isEnt('app') ? (
            bothEnt ? (
              <p className='pw-fieldhint'>Team seats are set organisation-wide below.</p>
            ) : (
              <div className='adm-grid'>
                <div className='adm-field'>
                  <div className='adm-field__label'>Seats (user limit)</div>
                  <input className='adm-input' type='number' min='1' value={model.access.appSeats} onChange={(e) => setAccess({ appSeats: e.target.value })} placeholder='e.g. 25' />
                  <span className='pw-fieldhint'>The org admin invites members up to this count.</span>
                </div>
              </div>
            )
          ) : (
            <p className='pw-fieldhint'>Trial grants the standard capped allowance. No seats to set.</p>
          )}
        </div>
      )}
      {model.products.api.on && (
        <div className='pw-block'>
          <div className='pw-block__h'>
            <span className='pw-dot pw-dot--api' /> Developer API
            <span className={`adm-badge ${isEnt('api') ? 'adm-badge--prod' : 'adm-badge--trial'}`}>{isEnt('api') ? 'Enterprise' : 'Trial'}</span>
          </div>
          {isEnt('api') ? (
            <div className='adm-grid'>
              {!bothEnt && (
                <div className='adm-field'>
                  <div className='adm-field__label'>Team seats</div>
                  <input className='adm-input' type='number' min='1' value={model.access.apiSeats} onChange={(e) => setAccess({ apiSeats: e.target.value })} placeholder='e.g. 5' />
                  <span className='pw-fieldhint'>People who can sign in to the API portal to manage keys and view usage — not API keys or call volume.</span>
                </div>
              )}
              <div className='adm-field'>
                <div className='adm-field__label'>Call limit (blank = uncapped)</div>
                <div className='pw-ig'>
                  <input className='adm-input' type='number' min='1' value={model.access.apiLimit} onChange={(e) => setAccess({ apiLimit: e.target.value })} placeholder='Uncapped' />
                  <select className='adm-input' value={model.access.apiInterval} onChange={(e) => setAccess({ apiInterval: e.target.value })}>
                    <option value='Monthly'>per month</option>
                    <option value='Annual'>per year</option>
                  </select>
                </div>
                <span className='pw-fieldhint'>API throughput — the metered call allowance. Separate from seats and API keys.</span>
              </div>
            </div>
          ) : (
            <p className='pw-fieldhint'>Trial grants 100 lifetime calls. No limit to set.</p>
          )}
        </div>
      )}
      {bothEnt && (
        <div className='pw-block'>
          <div className='pw-block__h'>Organisation team seats</div>
          <div className='adm-grid'>
            <div className='adm-field'>
              <div className='adm-field__label'>Team seats (organisation-wide)</div>
              <input className='adm-input' type='number' min='1' value={model.access.appSeats} onChange={(e) => setAccess({ appSeats: e.target.value })} placeholder='e.g. 25' />
              <span className='pw-fieldhint'>One login cap for the whole organisation — the people who can sign in to either portal. Not per product, and not the number of API keys.</span>
            </div>
          </div>
        </div>
      )}
      {anyEnterprise() && (
        <div className='pw-block'>
          <div className='pw-block__h'>Organisation admin</div>
          <label className='pw-check'>
            <input type='checkbox' checked={model.access.adminSame} onChange={(e) => setAccess({ adminSame: e.target.checked })} />
            <span>The primary contact ({model.org.email || 'the account holder'}) is the org admin</span>
          </label>
          {!model.access.adminSame && (
            <div className='adm-grid' style={{ marginTop: '.75rem' }}>
              <div className='adm-field' style={{ gridColumn: '1 / -1' }}>
                <div className='adm-field__label'>Admin email</div>
                <input className='adm-input' value={model.access.adminEmail} onChange={(e) => setAccess({ adminEmail: e.target.value })} placeholder='admin@company.com' />
              </div>
              <div className='adm-field'>
                <div className='adm-field__label'>Admin first name</div>
                <input className='adm-input' value={model.access.adminFirst} onChange={(e) => setAccess({ adminFirst: e.target.value })} />
              </div>
              <div className='adm-field'>
                <div className='adm-field__label'>Admin last name</div>
                <input className='adm-input' value={model.access.adminLast} onChange={(e) => setAccess({ adminLast: e.target.value })} />
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  const commEntBlock = (k, label, dot) =>
    isEnt(k) && (
      <div className='pw-block' key={k}>
        <div className='pw-block__h'>
          <span className={`pw-dot pw-dot--${dot}`} /> {label} — Enterprise terms
        </div>
        <div className='adm-grid'>
          <div className='adm-field'>
            <div className='adm-field__label'>Negotiated price</div>
            <div className='pw-ig'>
              <input className='adm-input' type='number' min='0' value={model.comm[k].price} onChange={(e) => setComm(k, { price: e.target.value })} placeholder='0' />
              <select className='adm-input' value={model.comm[k].interval} onChange={(e) => setComm(k, { interval: e.target.value })}>
                <option value='Annual'>per year</option>
                <option value='Monthly'>per month</option>
              </select>
            </div>
          </div>
          <div className='adm-field'>
            <div className='adm-field__label'>Purchase order</div>
            <input className='adm-input' value={model.comm[k].po} onChange={(e) => setComm(k, { po: e.target.value })} placeholder='PO-…' />
          </div>
          <div className='adm-field'>
            <div className='adm-field__label'>Contract start</div>
            <input className='adm-input' type='date' value={model.comm[k].start} onChange={(e) => setComm(k, { start: e.target.value })} />
          </div>
          <div className='adm-field'>
            <div className='adm-field__label'>Contract end</div>
            <input className='adm-input' type='date' value={model.comm[k].end} onChange={(e) => setComm(k, { end: e.target.value })} />
          </div>
          <div className='adm-field' style={{ gridColumn: '1 / -1' }}>
            <div className='adm-field__label'>Notes</div>
            <textarea className='adm-input' rows='2' value={model.comm[k].notes} onChange={(e) => setComm(k, { notes: e.target.value })} placeholder='Internal notes about this deal…' />
          </div>
        </div>
      </div>
    );

  const commStep = (
    <>
      <div className='pw-title'>Commercial terms</div>
      <p className='pw-hint'>Enterprise only — we set the price. Stored per product on the subscription.</p>
      {commEntBlock('app', 'RegulationCity App', 'app')}
      {commEntBlock('api', 'Developer API', 'api')}
    </>
  );

  const money = (v) => (v === '' || v == null ? '—' : `$${Number(v).toLocaleString()}`);
  const intervalWord = (i) => (i === 'Annual' ? 'year' : 'month');
  const reviewRow = (k, v) => (
    <div className='pw-rev__row'>
      <span className='pw-rev__k'>{k}</span>
      <span className='pw-rev__v'>{v}</span>
    </div>
  );

  const reviewStep = (
    <>
      <div className='pw-title'>Review &amp; provision</div>
      <p className='pw-hint'>Exactly what will be created.</p>
      <div className='pw-rev'>
        <div className='pw-rev__grp'>Organisation</div>
        {reviewRow('Name', model.org.name || '—')}
        {reviewRow('Primary contact', `${model.org.first} ${model.org.last} · ${model.org.email}`.trim())}
        {reviewRow('Domains', model.org.domains || '—')}
        {selected.map((p) => {
          const ent = isEnt(p.key);
          const c = model.comm[p.key];
          const seats = seatsFor(p.key);
          return (
            <React.Fragment key={p.key}>
              <div className='pw-rev__grp'>{p.name}</div>
              {reviewRow('Tier', ent ? 'Enterprise' : 'Trial')}
              {ent && !bothEnt && reviewRow(p.key === 'api' ? 'Team seats' : 'Seats', seats || '—')}
              {ent && p.key === 'api' &&
                reviewRow('Call limit', model.access.apiLimit === '' ? 'Uncapped' : `${Number(model.access.apiLimit).toLocaleString()} / ${intervalWord(model.access.apiInterval)}`)}
              {ent && reviewRow('Price', c.price === '' ? '—' : `${money(c.price)} / ${intervalWord(c.interval)}`)}
              {ent && (c.start || c.end) && reviewRow('Contract', `${c.start || '—'} → ${c.end || '—'}`)}
            </React.Fragment>
          );
        })}
        <div className='pw-rev__grp'>Admin &amp; onboarding</div>
        {bothEnt && reviewRow('Team seats (org-wide)', model.access.appSeats || '—')}
        {reviewRow('Org admin', model.access.adminSame ? `${model.org.email} (primary contact)` : model.access.adminEmail || '—')}
        {reviewRow('Onboarding', 'Set-password invite → admin invites members')}
        {prefill && prefill.enquiryId && reviewRow('Enquiry', 'Marked Handled on success')}
      </div>
      {error && <div className='adm-alert adm-alert--err' style={{ marginTop: '1rem' }}>{error}</div>}
      <div className='pw-ready'>✓ Ready to provision — {selected.length} product{selected.length > 1 ? 's' : ''}, {anyEnterprise() ? 'enterprise terms set' : 'trial only'}</div>
    </>
  );

  const bodies = [orgStep, productStep, accessStep, commStep, reviewStep];
  const nextLabels = [
    'Next: Products →',
    'Next: Access →',
    anyEnterprise() ? 'Next: Commercial →' : 'Next: Review →',
    'Next: Review →',
    busy ? 'Provisioning…' : '✓ Provision customer',
  ];
  const nextDisabled = (step === 0 && !orgValid) || (step === 1 && selected.length === 0) || busy;

  return (
    <section className='adm-section'>
      <p className='adm-sub'>
        Stand up a customer in one pass — organisation, products, seats, the org admin and the commercial
        terms. To manage existing customers, go to{' '}
        <Link to={`${ROUTES.APIAdmin}/accounts`} className='adm-link'>Customers →</Link>
      </p>
      <div className='adm-panel'>
        {stepper}
        <div className='pw-body'>{bodies[step]}</div>
        <div className='pw-foot'>
          <button className='adm-btn adm-btn--ghost' type='button' onClick={goBack} style={{ visibility: step === 0 ? 'hidden' : 'visible' }}>
            ← Back
          </button>
          <button
            className={`adm-btn ${step === 4 ? 'adm-btn--teal' : 'adm-btn--primary'}`}
            type='button'
            onClick={step === 4 ? submit : goNext}
            disabled={nextDisabled}
          >
            {nextLabels[step]}
          </button>
        </div>
      </div>
    </section>
  );
};

export default connect(mapStateToProps)(ProvisionWizard);
